import workerSource from "../pythonRealm/worker.js?raw";
import pyodideManifest from "../../scripts/pyodide-assets-manifest.json";
import {
    PythonRealmRefusal,
    configuredPythonRealmOrigin,
    createPythonRealmController,
} from "./pythonRealmClient.js";
import {
    DEFINITION_SOURCES,
    INVOCATION_SOURCES,
    RUN_SURFACES,
    evaluateKernelRunTrust,
} from "./kernelResolver.js";
import {
    reportKernelRunRealmTeardown,
    setKernelRunRealmAdapter,
} from "./kernelRunRealmAdapter.js";
import { pythonPackageReferenceRoots } from "./pythonPackageReferences.js";
import {
    isApprovedPythonPackage,
    isOnDemandPythonPackage,
    normalizePythonPackageRequirements,
} from "./pythonPackageRequirements.js";
import { createPythonWheelBroker } from "./pythonWheelBroker.js";
import { PYODIDE_LOCKED_PACKAGE_NAME_OVERRIDES } from "./pyodideImportCatalog.js";
import { onAuthIdentityChange } from "./authTokenStore.js";

export const PYTHON_REALM_KIND = Object.freeze({
    CHAT: "chat",
    PUBLISHED: "published",
});

const controllers = new Map();
const controllerPromises = new Map();
const controllerPackageScopes = new WeakMap();
let destroyedControllers = new WeakSet();
const MAX_RUNTIME_ASSET_BYTES = 64 * 1024 * 1024;
const MAX_RUNTIME_TOTAL_BYTES = 128 * 1024 * 1024;
let runtimeFilesPromise = null;
let controllerFactory = createDefaultController;

function hex(bytes) {
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function pythonRuntimeAssetPath(fileName) {
    const outputSubdirectory = String(pyodideManifest.outputSubdirectory || "")
        .replaceAll("\\", "/")
        .replace(/^\/+|\/+$/g, "");
    const segments = outputSubdirectory.split("/");
    if (
        !outputSubdirectory
        || segments.some((segment) => !segment || segment === "." || segment === "..")
        || typeof fileName !== "string"
        || !fileName
        || fileName.includes("/")
        || fileName.includes("\\")
    ) {
        throw new PythonRealmRefusal("runtime_asset_manifest_invalid");
    }
    return `/${outputSubdirectory}/${encodeURIComponent(fileName)}`;
}

export function pythonRuntimeAssetUrl(
    fileName,
    origin = configuredPythonRealmOrigin(),
) {
    return `${origin}${pythonRuntimeAssetPath(fileName)}`;
}

async function readExactRuntimeAsset(response, expectedSize) {
    if (
        !Number.isSafeInteger(expectedSize)
        || expectedSize <= 0
        || expectedSize > MAX_RUNTIME_ASSET_BYTES
    ) {
        throw new PythonRealmRefusal("runtime_asset_manifest_invalid");
    }
    const reader = response.body?.getReader?.();
    if (!reader) {
        throw new PythonRealmRefusal("runtime_asset_verification_unavailable");
    }

    const bytes = new Uint8Array(expectedSize);
    let offset = 0;
    while (true) {
        // Runtime files are deliberately read one stream at a time. A fixed
        // destination makes a corrupt response unable to grow memory beyond
        // its checked manifest size.
        const chunk = await reader.read();
        if (chunk.done) break;
        if (!(chunk.value instanceof Uint8Array) || offset + chunk.value.byteLength > expectedSize) {
            try { await reader.cancel(); } catch { /* best effort */ }
            throw new PythonRealmRefusal("runtime_asset_verification_failed");
        }
        bytes.set(chunk.value, offset);
        offset += chunk.value.byteLength;
    }
    if (offset !== expectedSize) {
        throw new PythonRealmRefusal("runtime_asset_verification_failed");
    }
    return bytes.buffer;
}

export async function loadVerifiedRuntimeFiles() {
    if (!runtimeFilesPromise) {
        const pending = (async () => {
            if (!globalThis.crypto?.subtle) {
                throw new PythonRealmRefusal("runtime_verification_unavailable");
            }

            const files = Object.create(null);
            let totalBytes = 0;
            for (const asset of pyodideManifest.assets) {
                if (
                    asset.kind === "package"
                    || asset.kind === "on-demand-package"
                ) {
                    continue;
                }
                totalBytes += asset.size;
                if (
                    !Number.isSafeInteger(totalBytes)
                    || totalBytes > MAX_RUNTIME_TOTAL_BYTES
                ) {
                    throw new PythonRealmRefusal("runtime_asset_manifest_invalid");
                }
                const url = pythonRuntimeAssetUrl(asset.fileName);
                const response = await fetch(url, {
                    credentials: "omit",
                    mode: "cors",
                    cache: "force-cache",
                });
                if (!response.ok) {
                    throw new PythonRealmRefusal("runtime_asset_unavailable");
                }
                const bytes = await readExactRuntimeAsset(response, asset.size);
                const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
                if (hex(new Uint8Array(digest)) !== asset.sha256) {
                    throw new PythonRealmRefusal("runtime_asset_verification_failed");
                }
                files[asset.fileName] = bytes;
            }
            return files;
        });
        runtimeFilesPromise = pending();
        const current = runtimeFilesPromise;
        current.then(
            () => {
                if (runtimeFilesPromise === current) runtimeFilesPromise = null;
            },
            () => {
                if (runtimeFilesPromise === current) runtimeFilesPromise = null;
            },
        );
    }
    return runtimeFilesPromise;
}

async function createDefaultController({ packageNames }) {
    const runtimeFiles = await loadVerifiedRuntimeFiles();
    return createPythonRealmController({
        workerSource,
        runtimeFiles,
        packageScope: packageNames,
        onRealmTeardown: reportKernelRunRealmTeardown,
    });
}

function destroyController(key, controller) {
    if (controllers.get(key) === controller) controllers.delete(key);
    if (controller && !destroyedControllers.has(controller)) {
        destroyedControllers.add(controller);
        controller.destroy?.();
    }
}

function destroyAuthBoundControllers() {
    // Clearing pending entries invalidates their completion check in
    // controllerFor; a controller that finishes construction afterward is
    // immediately destroyed instead of entering the new identity's pool.
    controllerPromises.clear();
    for (const [key, controller] of controllers) destroyController(key, controller);
    controllers.clear();
}

onAuthIdentityChange(destroyAuthBoundControllers);

function packageScopesEqual(left, right) {
    return (
        Array.isArray(left)
        && left.length === right.length
        && left.every((packageName, index) => packageName === right[index])
    );
}

async function controllerFor(key, descriptor) {
    const existing = controllers.get(key);
    if (existing) {
        if (!packageScopesEqual(
            controllerPackageScopes.get(existing),
            descriptor.packageNames,
        )) {
            destroyController(key, existing);
        } else {
            try {
                await existing.whenReady();
                return existing;
            } catch {
                destroyController(key, existing);
            }
        }
    }

    let pending = controllerPromises.get(key);
    if (!pending) {
        pending = Promise.resolve(controllerFactory(descriptor))
            .then((controller) => {
                if (!controller || typeof controller.runPython !== "function") {
                    throw new PythonRealmRefusal("realm_controller_invalid");
                }
                if (controllerPromises.get(key) !== pending) {
                    destroyController(key, controller);
                    throw new PythonRealmRefusal("realm_controller_superseded");
                }
                controllerPackageScopes.set(
                    controller,
                    Object.freeze([...descriptor.packageNames]),
                );
                controllers.set(key, controller);
                return controller;
            })
            .finally(() => {
                if (controllerPromises.get(key) === pending) {
                    controllerPromises.delete(key);
                }
        });
        controllerPromises.set(key, pending);
    }
    const controller = await pending;
    if (!packageScopesEqual(
        controllerPackageScopes.get(controller),
        descriptor.packageNames,
    )) {
        destroyController(key, controller);
        return controllerFor(key, descriptor);
    }
    return controller;
}

function hasPythonWheelAuthority(evaluated) {
    return evaluated.definitionSource === DEFINITION_SOURCES.LIVE
        && evaluated.invocationSource === INVOCATION_SOURCES.LIVE
        && evaluated.runContext.surface !== RUN_SURFACES.CANVAS;
}

function realmKindFor(evaluated) {
    // This partition is the hard state boundary. Trusted live/bundled
    // non-canvas code cannot inherit module state poisoned by stored/unknown
    // code; every canvas stays on the non-authoritative side even when its
    // definition is bundled.
    return evaluated.trusted
        && evaluated.runContext.surface !== RUN_SURFACES.CANVAS
        ? PYTHON_REALM_KIND.CHAT
        : PYTHON_REALM_KIND.PUBLISHED;
}

function declaredPackageNamesForCode(code) {
    const packageNames = new Set();
    for (const importRoot of pythonPackageReferenceRoots(code)) {
        const packageName = PYODIDE_LOCKED_PACKAGE_NAME_OVERRIDES[importRoot]
            || importRoot;
        if (isApprovedPythonPackage(packageName)) {
            packageNames.add(packageName);
        }
    }
    return [...packageNames].sort();
}

export { normalizePythonPackageRequirements };

function assertCodeRequirementsDeclared(code, packageNames) {
    const declared = new Set(packageNames);
    const undeclared = declaredPackageNamesForCode(code)
        .filter((packageName) => !declared.has(packageName));
    if (undeclared.length > 0) {
        const refusal = new PythonRealmRefusal("package_requirement_undeclared");
        refusal.missingPackages = undeclared;
        throw refusal;
    }
}

function controllerRoute(evaluated, executionId, executionPackageNames) {
    const kind = realmKindFor(evaluated);
    if (kind === PYTHON_REALM_KIND.CHAT) {
        return {
            key: kind,
            kind,
            packageNames: executionPackageNames,
        };
    }
    if (typeof executionId !== "string" || !executionId.trim()) {
        throw new PythonRealmRefusal("execution_id_required");
    }
    return {
        key: `${PYTHON_REALM_KIND.PUBLISHED}:${executionId}`,
        kind,
        packageNames: executionPackageNames,
    };
}

function unavailable(error) {
    return {
        ok: false,
        outcome: "unavailable",
        unavailable: true,
        code: error?.code || "python_realm_unavailable",
        reason: error?.reason || "python_realm_unavailable",
        error: error?.message || "Isolated Python is unavailable for this run.",
    };
}

export async function runPythonInRealm({
    resolvedDefinition,
    runContext,
    code,
    globals = {},
    attribution = {},
    onWheelRequest,
    signal,
    runId,
    executionId,
    timeoutMs,
    onLaunched,
    mode,
    onHostCall,
    packageRequirements,
    executionPackageRequirements,
} = {}) {
    if (mode === "async" || typeof onHostCall === "function") {
        return {
            ok: false,
            outcome: "refused",
            refused: true,
            error: "A host bridge is not available to isolated Python.",
        };
    }

    let controller;
    let key;
    try {
        const evaluated = evaluateKernelRunTrust(resolvedDefinition, runContext);
        const packageNames = normalizePythonPackageRequirements(packageRequirements);
        const executionPackageNames = normalizePythonPackageRequirements(
            executionPackageRequirements ?? packageNames,
        );
        if (packageNames.some((name) => !executionPackageNames.includes(name))) {
            throw new PythonRealmRefusal("package_requirements_invalid");
        }
        assertCodeRequirementsDeclared(code, packageNames);

        const route = controllerRoute(
            evaluated,
            executionId || runId,
            executionPackageNames,
        );
        key = route.key;
        const { kind } = route;
        const requiresOnDemandConsent = packageNames.some(
            (name) => isOnDemandPythonPackage(name),
        );
        const wheelAuthority = hasPythonWheelAuthority(evaluated);
        const mayLoadDeclaredPackages = !requiresOnDemandConsent || wheelAuthority;

        controller = await controllerFor(key, {
            kind,
            packageNames: route.packageNames,
        });
        const result = await controller.runPython({
            code,
            globals,
            attribution: {
                ...(attribution && typeof attribution === "object" ? attribution : {}),
                realmKind: kind,
                surface: evaluated.runContext.surface,
                runTrust: {
                    trusted: evaluated.trusted,
                    wheelAuthority,
                    definitionSource: evaluated.definitionSource,
                    invocationSource: evaluated.invocationSource,
                },
                ...(packageNames.length > 0 ? { packageNames } : {}),
            },
            onWheelRequest: packageNames.length > 0 && mayLoadDeclaredPackages
                ? (onWheelRequest || createPythonWheelBroker())
                : undefined,
            requiredWheelPackages: packageNames,
            signal,
            runId,
            timeoutMs,
            onLaunched,
        });
        return {
            ok: true,
            result,
            stdout: "",
            stderr: "",
            realmKind: kind,
            namespaceGeneration: controller.generation,
        };
    } catch (error) {
        if (key && controller && error instanceof PythonRealmRefusal) {
            destroyController(key, controller);
        }
        if (
            error instanceof PythonRealmRefusal
            && [
                "execution_id_required",
                "package_requirement_undeclared",
                "package_requirements_invalid",
            ].includes(error.reason)
        ) {
            return {
                ok: false,
                outcome: "refused",
                refused: true,
                reason: error.reason,
                error: error.message,
                ...(Array.isArray(error.missingPackages)
                    ? { missingPackages: [...error.missingPackages] }
                    : {}),
            };
        }
        if (error instanceof PythonRealmRefusal) return unavailable(error);
        if (error?.outcome === "cancelled" || signal?.aborted) {
            return {
                ok: false,
                outcome: "cancelled",
                cancelled: true,
                reason: error?.reason || "run_cancelled",
                error: error?.message || "Python execution was cancelled.",
            };
        }
        if (error?.outcome === "refused") {
            return {
                ok: false,
                outcome: "refused",
                refused: true,
                reason: error.reason || "python_execution_refused",
                error: error.message || "Python execution was refused.",
            };
        }
        return {
            ok: false,
            outcome: "failed",
            error: error?.message || String(error),
        };
    }
}

export function setPythonRealmControllerFactory(factory) {
    if (typeof factory !== "function") {
        throw new TypeError("Python realm controller factory must be a function");
    }
    const previous = controllerFactory;
    controllerFactory = factory;
    return () => {
        if (controllerFactory === factory) controllerFactory = previous;
    };
}

export function terminatePythonRunRealm(context = {}) {
    const results = [];
    for (const [key, controller] of controllers) {
        const result = controller.terminateRunRealm(context);
        if (result.terminated) {
            destroyController(key, controller);
            results.push(result);
        }
    }
    return {
        terminated: results.some((result) => result.terminated),
        cancelledRunIds: [
            ...new Set(results.flatMap((result) => result.cancelledRunIds || [])),
        ],
    };
}

export function releasePythonExecutionRealm(context = {}) {
    const executionId = typeof context === "string"
        ? context
        : context?.executionId || context?.runId;
    if (typeof executionId !== "string" || !executionId.trim()) {
        return { released: false };
    }
    const key = `${PYTHON_REALM_KIND.PUBLISHED}:${executionId}`;
    const controller = controllers.get(key);
    controllerPromises.delete(key);
    if (!controller) return { released: false };
    destroyController(key, controller);
    return { released: true };
}

setKernelRunRealmAdapter({
    releaseRunRealm: releasePythonExecutionRealm,
    terminateRunRealm: terminatePythonRunRealm,
});

export function _resetPythonRealmRuntimeForTests() {
    destroyAuthBoundControllers();
    destroyedControllers = new WeakSet();
    runtimeFilesPromise = null;
    controllerFactory = createDefaultController;
}
